import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBullseye, faFlag, faHandsHelping, faChurch } from '@fortawesome/free-solid-svg-icons';

const YoungAdults = () => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleNavbar = () => {
        setIsOpen(!isOpen);
    };

    const sections = [
        { id: 'Objectives', title: 'Philosophy & Objectives' },
        { id: 'Mission-Aim-Motto-And-Pledge', title: 'Mission, Aim & Pledge' },
        { title: 'Adventurers', href: 'Adventurers' },
        { title: 'Pathfinders', href: 'Pathfinders' },
        { title: 'Ambassadors', href: 'Ambassadors' },
        { title: 'PCM', href: 'PCM' }
    ];

    return (
        <>
            <div className="min-h-screen bg-gradient-to-b from-sky-50 to-blue-50">
                <Header />
                
                <main className="pt-24">
                    <div className="max-w-7xl mx-auto px-4">
                        {/* Navigation */}
                        <nav className="mb-12">
                            <div className="flex flex-wrap gap-2 justify-center">
                                {sections.map((section, idx) => (
                                    section.href ? (
                                        <a
                                            key={idx}
                                            href={section.href}
                                            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:from-indigo-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-300 shadow-lg"
                                        >
                                            {section.title}
                                        </a>
                                    ) : (
                                        <a
                                            key={idx}
                                            href={`#${section.id}`}
                                            className="bg-gradient-to-r from-blue-600 to-sky-600 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-sky-700 transform hover:scale-105 transition-all duration-300 shadow-lg"
                                        >
                                            {section.title}
                                        </a>
                                    )
                                ))}
                            </div>
                        </nav>

                        {/* Objectives */}
                        <section id="Objectives" className="scroll-mt-24 mb-20">
                            <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-2xl p-8">
                                <div className="flex items-center mb-8">
                                    <div className="bg-gradient-to-r from-blue-500 to-sky-500 p-3 rounded-xl mr-4">
                                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-800">Objectives</h2>
                                </div>
                                
                                <div className="space-y-6 text-gray-600 leading-relaxed">
                                    <p className="text-lg">
                                        "To save from sin and guide into service:" this true and only motive, so complete and impressive, was adopted during the 1926 General Conference session.
                                    </p>
                                    
                                    <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-2xl p-6">
                                        <p className="font-semibold text-gray-700 mb-4">That the youth may work for:</p>
                                        <div className="grid md:grid-cols-3 gap-4">
                                            {['Other youth', 'Their church', 'Their fellow men'].map((item, idx) => (
                                                <div key={idx} className="flex items-center bg-white p-4 rounded-xl shadow-sm">
                                                    <FontAwesomeIcon icon={faCheck} className="text-blue-500 mr-3" />
                                                    <span className="text-gray-700 font-medium">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        <p>This triple purpose that God has outlined for His youth is really the second part of the objective presented in a practical sense: "guide into service." From the time youth missionary work first began, this triple purpose has been put into practice.</p>
                                        
                                        <div className="grid md:grid-cols-2 gap-6 mt-8">
                                            <div className="bg-white rounded-xl p-5 shadow-lg">
                                                <h3 className="font-bold text-gray-800 mb-3">Working for Other Youth</h3>
                                                <p className="text-gray-600 text-sm">
                                                    "Educate the youth to help the youth; and in seeking to do this work each will gain experience that will qualify him to become a consecrated worker in a larger sphere."
                                                </p>
                                            </div>
                                            
                                            <div className="bg-white rounded-xl p-5 shadow-lg">
                                                <h3 className="font-bold text-gray-800 mb-3">Working for the Church</h3>
                                                <p className="text-gray-600 text-sm">
                                                    "Loyalty to Christ demands the faithful performance of church duties. The church is languishing for the help of young men who will bear a courageous testimony."
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Mission, Aim, Motto and Pledge */}
                        <section id="Mission-Aim-Motto-And-Pledge" className="scroll-mt-24 mb-20">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold text-gray-800 mb-4">Mission, Aim, Motto and Pledge</h2>
                                <div className="w-32 h-1 bg-gradient-to-r from-sky-500 to-blue-500 mx-auto"></div>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    {
                                        icon: faBullseye,
                                        title: 'Mission Statement',
                                        content: 'To lead young people into a saving relationship with Jesus Christ and help them embrace His call to discipleship.',
                                        color: 'from-sky-500 to-blue-500'
                                    },
                                    {
                                        icon: faFlag,
                                        title: 'Aim',
                                        content: 'The Advent Message to All the World in My Generation',
                                        color: 'from-blue-500 to-indigo-500'
                                    },
                                    {
                                        icon: faHandsHelping,
                                        title: 'Motto',
                                        content: 'The Love of Christ Compels Me.',
                                        color: 'from-purple-500 to-pink-500'
                                    },
                                    {
                                        icon: faChurch,
                                        title: 'Pledge',
                                        content: 'Loving the Lord Jesus, I promise to take an active part in the youth ministry of the church, doing what I can to help others and to finish the work of the Gospel in all the world.',
                                        color: 'from-emerald-500 to-green-500'
                                    }
                                ].map((item, idx) => (
                                    <div key={idx} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 transform hover:-translate-y-2 transition-all duration-300 h-full">
                                        <div className={`bg-gradient-to-r ${item.color} p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4`}>
                                            <FontAwesomeIcon icon={item.icon} className="text-white text-xl" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">{item.title}</h3>
                                        <p className="text-gray-600 text-center">{item.content}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Programs & Activities */}
                        <div className="bg-gradient-to-br from-white to-sky-50 rounded-3xl shadow-2xl p-8 mb-20">
                            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Young Adults Programs</h2>
                            
                            <div className="grid md:grid-cols-3 gap-6">
                                {[
                                    {
                                        title: 'Spiritual Growth',
                                        items: ['Bible Study Groups', 'Prayer Meetings', 'Discipleship Training', 'Retreats & Conferences'],
                                        color: 'border-l-4 border-sky-500'
                                    },
                                    {
                                        title: 'Social Connection',
                                        items: ['Fellowship Events', 'Community Service', 'Recreational Activities', 'Networking'],
                                        color: 'border-l-4 border-blue-500'
                                    },
                                    {
                                        title: 'Leadership',
                                        items: ['Mentorship Programs', 'Skill Development', 'Ministry Training', 'Project Management'],
                                        color: 'border-l-4 border-indigo-500'
                                    }
                                ].map((program, idx) => (
                                    <div key={idx} className={`bg-white rounded-2xl p-6 shadow-lg ${program.color}`}>
                                        <h3 className="text-xl font-bold text-gray-800 mb-4">{program.title}</h3>
                                        <ul className="space-y-3">
                                            {program.items.map((item, itemIdx) => (
                                                <li key={itemIdx} className="flex items-center">
                                                    <div className="w-2 h-2 bg-gradient-to-r from-sky-500 to-blue-500 rounded-full mr-3"></div>
                                                    <span className="text-gray-600">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Call to Action */}
                        <div className="relative rounded-3xl overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-sky-600 to-blue-600"></div>
                            <div className="relative z-10 p-12 text-center">
                                <h2 className="text-4xl font-bold text-white mb-6">Join Our Young Adults Ministry</h2>
                                <p className="text-sky-100 text-lg mb-8 max-w-2xl mx-auto">
                                    Connect with other young adults, grow in your faith, and make a difference in your community.
                                </p>
                                <div className="flex flex-wrap gap-4 justify-center">
                                    <a 
                                        href="#"
                                        className="bg-white text-blue-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg"
                                    >
                                        Get Involved
                                    </a>
                                    <a 
                                        href="#"
                                        className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white/10 transform hover:scale-105 transition-all duration-300"
                                    >
                                        Find a Group
                                    </a>
                                </div>
                            </div>
                            
                            {/* Decorative elements */}
                            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mt-20 -mr-20"></div>
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -mb-16 -ml-16"></div>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default YoungAdults;